import { AGENT_HUB } from '@lumapps/agent-hub/keys';
import { GLOBAL } from '@lumapps/translations';
import { TranslatableObject } from '@lumapps/translations/types';

import { AGENT_HUB_AGENTS } from './keys';
import {
    type AgentFormDefaultValues,
    type AgentStatusOption,
    type AgentTypeOption,
    AgentStatus,
    AgentType,
} from './types';

/** Scope used for the agents admin */
export const SCOPE = 'search-agents-admin';

export const SEARCH_AGENTS_LIST_DEFAULT_SORT_ORDER = '-status,name';

/** Default max items fetched when fetching agents */
export const SEARCH_AGENTS_LIST_MAX_RESULTS = 20;

/** Minimum number of conversation starters required */
export const MIN_CONVERSATION_STARTERS = 1;

/** Maximum number of conversation starters allowed */
export const MAX_CONVERSATION_STARTERS = 4;

/** Maximum character length for a single conversation starter */
export const MAX_CONVERSATION_STARTER_LENGTH = 80;

/** Maximum number of tools to add to an agent before displaying a context warning */
export const MAX_TOOLS_BEFORE_WARNING = 10;

/** Empty conversation starter object template */
export const EMPTY_CONVERSATION_STARTER: TranslatableObject<string> = { translations: {} };

/** Default icon name for new agents */
export const DEFAULT_AGENT_ICON_NAME = 'robot-outline';

/** Default icon background color for new agents */
export const DEFAULT_AGENT_ICON_BACKGROUND_COLOR = 'rgba(0, 0, 0, 0.1)';

/** Default values for creating a new agent (type is selected by user) */
export const SEARCH_AGENT_DEFAULT_VALUES: AgentFormDefaultValues = {
    title: '',
    icon: { name: DEFAULT_AGENT_ICON_NAME, path: '' },
    visibleByGroups: [],
    status: AgentStatus.DRAFT,
    conversationStarters: [EMPTY_CONVERSATION_STARTER],
};

export const AGENT_NAME_MAX_LENGTH = 50;

export const MAXIMUM_SOURCES = 4;

export const AGENT_TYPE_MISSION = {
    [AgentType.EXTERNAL]: AGENT_HUB.LIST_AN_EXTERNAL_AGENT_IN_THE_LIBRARY,
    [AgentType.RAG]: AGENT_HUB.SPECIALIZED_SEARCH_ASSISTANT_FOR_ASK_AI,
    [AgentType.REACT_STATELESS]: AGENT_HUB.AI_AGENT,
    [AgentType.STATIC_ANSWER]: AGENT_HUB_AGENTS.PROVIDE_CUSTOM_ANSWER,
};

/**
 * Create front-office grouped agent type options
 * - "AI agents": REACT_STATELESS + EXTERNAL
 * - "AI search assistants": RAG
 * - STATIC_ANSWER excluded
 */
export const createFrontOfficeAgentTypeOptions = (translateKey: (key: string) => string): AgentTypeOption[] => [
    {
        id: 'ai-agents',
        label: translateKey(AGENT_HUB_AGENTS.AI_AGENTS),
        types: [AgentType.REACT_STATELESS, AgentType.EXTERNAL],
        isGroup: true,
    },
    {
        id: 'ai-search-assistants',
        label: translateKey(AGENT_HUB_AGENTS.AI_SEARCH_ASSISTANTS),
        types: [AgentType.RAG],
        isGroup: false,
    },
];

/**
 * Create back-office agent type options
 * All 4 agent types as individual options (no grouping)
 */
export const createBackOfficeAgentTypeOptions = (translateKey: (key: string) => string): AgentTypeOption[] => [
    {
        id: AgentType.RAG,
        label: translateKey(AGENT_HUB.SPECIALIZED_SEARCH_ASSISTANT_FOR_ASK_AI),
        types: [AgentType.RAG],
    },
    {
        id: AgentType.REACT_STATELESS,
        label: translateKey(AGENT_HUB.AI_AGENT),
        types: [AgentType.REACT_STATELESS],
    },
    {
        id: AgentType.STATIC_ANSWER,
        label: translateKey(AGENT_HUB_AGENTS.PROVIDE_CUSTOM_ANSWER),
        types: [AgentType.STATIC_ANSWER],
    },
    {
        id: AgentType.EXTERNAL,
        label: translateKey(AGENT_HUB.LIST_AN_EXTERNAL_AGENT_IN_THE_LIBRARY),
        types: [AgentType.EXTERNAL],
    },
];

/**
 * Create agent status filter options
 */
export const createAgentStatusOptions = (translateKey: (key: string) => string): AgentStatusOption[] => [
    {
        id: AgentStatus.LIVE,
        label: translateKey(GLOBAL.PUBLISHED),
    },
    {
        id: AgentStatus.DRAFT,
        label: translateKey(GLOBAL.DRAFT),
    },
];

/**
 * Some agents are "static" and needs to be initiated by the frontend.
 * For these, we need to directly target their IDS.
 */
export const NATIVE_AGENTS = {
    askAi: {
        id: '0af756ae-3e8d-4bd1-9724-16e45ebc1908',
    },
    learning: {
        id: 'c839a10c-ee83-41a9-adf9-bb80533dbcb3',
    },
    activity: {
        id: '633194fb-adf9-48c2-bfcc-ea6ca43d45e2',
    },
    broadcast: {
        id: 'bcc7af44-eedc-4eab-9c35-ccd8d2995084',
    },
    pageWriter: {
        id: '73d8f32c-0842-4df1-84f6-223417796fe0',
    },
    pageWriterDesigner: {
        id: '0c501995-74bf-4793-9634-a16ead1c72c2',
    },
    playVideo: {
        id: '7b3823cb-8f15-5fd8-afcb-d2d49f4348d1',
    },
} as const;

/** Array of all agent types possible */
export const AGENT_TYPES_ARRAY = Object.values(AgentType);
